import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import { API, BACKEND_URL } from '@/config';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Calendar, User, ChevronLeft, ChevronRight, Image, BookOpen } from 'lucide-react';

const BlogPage = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchPosts();
  }, [page]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/blog?page=${page}&limit=12`);
      setPosts(response.data.posts);
      setTotalPages(response.data.pages);
    } catch (error) {
      toast.error('Error al cargar los posts');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const getFirstImage = (post) => {
    if (post.images && post.images.length > 0) {
      const img = post.images[0];
      return img.startsWith('/api') ? `${BACKEND_URL}${img}` : img;
    }
    return null;
  };

  const getExcerpt = (post, maxLen = 150) => {
    const source = post.meta_description || post.content;
    const text = source
      .replace(/^[a-zA-Z_]+:.*\n/gm, '') // stray frontmatter lines, if any slipped through
      .replace(/<[^>]*>/g, '')
      .replace(/^#+\s*/gm, '')           // markdown heading markers
      .replace(/[*_`>~-]/g, '')          // common markdown emphasis/list/quote symbols
      .replace(/\n+/g, ' ')
      .trim();
    return text.length > maxLen ? text.substring(0, maxLen) + '...' : text;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50" data-testid="blog-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <BookOpen className="w-4 h-4" />
            Comunidad Nazca360
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-amber-900 mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Blog de la Comunidad
          </h1>
          <p className="text-amber-800/80 text-lg max-w-2xl mx-auto">
            Descubre y comparte experiencias sobre las Líneas de Nazca
          </p>
          <div className="flex items-center justify-center gap-2 mt-6">
            <div className="h-px w-20 bg-gradient-to-r from-transparent to-amber-400"></div>
            <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
            <div className="h-px w-20 bg-gradient-to-l from-transparent to-amber-400"></div>
          </div>

          {token && (
            <div className="mt-8">
              <button
                data-testid="create-post-btn"
                onClick={() => navigate('/blog/new')}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white px-6 py-3 rounded-full transition-all font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                <Plus size={18} />
                Nuevo Post
              </button>
            </div>
          )}
        </div>

        {/* Posts Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 bg-white/60 backdrop-blur rounded-3xl border-2 border-amber-200" data-testid="no-posts">
            <Image size={48} className="mx-auto text-amber-400 mb-4" />
            <p className="text-amber-900 text-lg font-medium">No hay posts aún</p>
            {token && (
              <button
                onClick={() => navigate('/blog/new')}
                className="mt-4 text-amber-700 hover:text-amber-900 underline font-medium"
              >
                Sé el primero en publicar
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="posts-grid">
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.id}`}
                data-testid={`post-card-${post.id}`}
                className="group bg-white rounded-2xl overflow-hidden border-2 border-amber-100 hover:border-amber-300 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                {/* Post Image */}
                {getFirstImage(post) ? (
                  <div className="h-48 overflow-hidden bg-amber-100">
                    <img
                      src={getFirstImage(post)}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                ) : (
                  <div className="h-48 bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
                    <Image size={40} className="text-amber-400" />
                  </div>
                )}
                {/* Post Content */}
                <div className="p-5">
                  {post.category && (
                    <span className="inline-block px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-[11px] font-semibold uppercase tracking-wide mb-2">
                      {post.category}
                    </span>
                  )}
                  <h2
                    className="text-xl font-bold text-amber-900 group-hover:text-amber-700 transition-colors line-clamp-2"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {post.title}
                  </h2>
                  <p className="text-gray-600 text-sm mt-2 line-clamp-3">
                    {getExcerpt(post)}
                  </p>
                  <div className="flex items-center gap-4 mt-4 text-xs text-amber-700/70 pt-3 border-t border-amber-100">
                    <span className="flex items-center gap-1">
                      <User size={12} />
                      {post.author_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {formatDate(post.created_at)}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-10" data-testid="pagination">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 rounded-full bg-white border-2 border-amber-200 text-amber-700 disabled:opacity-30 hover:bg-amber-50 transition-colors shadow-sm"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="text-amber-900 text-sm font-medium">
              Página {page} de {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 rounded-full bg-white border-2 border-amber-200 text-amber-700 disabled:opacity-30 hover:bg-amber-50 transition-colors shadow-sm"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
